import React, { useState, useContext, useEffect } from 'react';
import { Trans } from '@lingui/react';
import MetaTags from '@components/MetaTags';
import { Container } from '@components/layout/Grid';
import ProgramsSubHeader from '@ecosystems/navigations/ProgramsSubHeader';
import OverviewWeekCollapsable from '@ecosystems/programs/ProgramOverviewWeekCollapsable';
import ProgramIntroductionCollapsable from '@ecosystems/programs/PhasedProgramIntroductionCollapsable';
import ProgramOutroCollapsable from '@ecosystems/programs/PhasedProgramOutroCollapsable';
import AccessProgramHeaderSection from '@ecosystems/programs/AccessProgramHeaderSection';
import EmptyProgram from '@skeletons/ProgressPageSkeleton';
import GuestProgramHeader from '@ecosystems/programs/GuestProgramHeaderSection';
import ProgramsBreadcrumb from '@ecosystems/programs/ProgramsBreadcrumb';
import { canBuyProgramGift, canExtendProgramAccess } from '@lib/helpers';
import { UserContext } from '@lib/contexts/UserProvider';
import LanguageContext from '@lib/contexts/languageContext';
import {
  GenericProgram,
  ProgramAccessStatus,
  ProgramCategory,
} from '@gql/generated';
import ProgramPartsBar from '@organisms/ProgramPartsBar';
import ProgramExpirationBanner from '@ecosystems/programs/ProgramExpirationBanner';
import { durations } from '@lib/constants';
import { useWeeklyProgramQuery, WeeklyProgramQuery } from '@gql/generated';
import { getStartedLink } from '../progress/WeeklyProgramProgress';
import AnalyticsManager from '@lib/analytics/manager';

// const GlobalStyle = createGlobalStyle<{ category: ProgramCategory }>`
//   --program-primary-color: ${({ category, theme }) => {
//     switch (category) {
//       case ProgramCategory['Courses']:
//         return theme.colors.primary.green;
//       case ProgramCategory['Challenges']:
//         return theme.colors.secondary.gold;
//       default:
//         return theme.colors.primary.stone;
//     }
//   }};
// `;

export const getButtonAppearance = (category: ProgramCategory) => {
  switch (category) {
    case ProgramCategory['Courses']:
      return 'stone';
    case ProgramCategory['Challenges']:
      return 'green';
    default:
      return 'stone';
  }
};

type Props = {
  className?: string;
  programSlug: string;
  baseProgram: GenericProgram;
  program: WeeklyProgramQuery['weeklyProgram'];
};

const CourseOverview = (props: Props) => {
  const { programSlug, baseProgram, className } = props;
  const [activePhase, setActivePhase] = useState(0);
  const lang = useContext(LanguageContext);
  const [currentUser] = useContext(UserContext);
  const {
    data: programData,
    loading: programLoading,
    error: programError,
  } = useWeeklyProgramQuery({
    variables: { slug: programSlug },
    ssr: false,
  });

  const onCollapsableChange = (index) => {
    setActivePhase(index);
  };

  const onCollapsableOpen = (index) => {
    setActivePhase(index);
  };

  const onCollapsableClose = () => {
    setActivePhase(null);
  };

  const program = programData?.weeklyProgram || props.program;

  useEffect(() => {
    if (
      baseProgram.id &&
      baseProgram.category === ProgramCategory['Courses'] &&
      !baseProgram?.programAccess
    ) {
      const new_customer =
        (currentUser?.extraPayments?.length || 0) +
          (currentUser?.receipts?.length || 0) ===
        0;

      AnalyticsManager().sendItemViewEvent({
        category: 'Course',
        slug: program.slug,
        title: baseProgram.title,
        price: Math.floor(baseProgram.price.originalPrice / 100),
        currency: baseProgram.price.currency,
        new_customer,
        logged_in: !!currentUser?.id,
      });
    }
  }, [baseProgram?.id]);

  if (programError || (!program && programLoading)) {
    return <EmptyProgram key="EmptyProgram" />;
  }

  const programLanguage = baseProgram?.language || lang;
  const programWeeks = program.weeks;

  const userProgramAccess = baseProgram.programAccess;

  const userHasAccess = [
    ProgramAccessStatus['Active'],
    ProgramAccessStatus['ExpiringSoon'],
  ].includes(baseProgram?.accessStatus);

  let collapsables = [];
  if (program?.intro) {
    collapsables.push(
      <ProgramIntroductionCollapsable
        key="intro"
        programCategory={baseProgram.category}
        data={program.intro}
        videosCount={program['videosCount']}
        weeksCount={program?.weeks?.length}
        isOpen={activePhase === 0}
        onOpen={() => onCollapsableOpen(0)}
        onClose={() => onCollapsableClose()}
        programDuration={
          baseProgram.duration?.value ? (
            <Trans
              id="program.duration"
              values={{
                value: baseProgram.duration?.value,
                unit: <Trans id={durations[baseProgram.duration?.unit]} />,
              }}
            />
          ) : null
        }
      />
    );
  }

  collapsables = collapsables.concat(
    ...programWeeks.map((_week, i) => {
      const week = { ..._week };
      week['videosDuration'] = week['duration'];
      week['weeksRange'] = `Vecka ${week['_weeksRange']}`;

      const index = collapsables.length + i;
      return (
        <OverviewWeekCollapsable
          key={week.id}
          index={i}
          programSlug={program.slug}
          week={week}
          userHasProgramAccess={userHasAccess}
          programLanguage={programLanguage}
          programCategory={baseProgram.category}
          isOpen={activePhase === index}
          onOpen={() => onCollapsableOpen(index)}
          onClose={() => onCollapsableClose()}
        />
      );
    })
  );

  if (program.outro) {
    const position = collapsables.length + 1;
    collapsables.push(
      <ProgramOutroCollapsable
        key="outro"
        programLanguage={programLanguage}
        genericProgram={baseProgram}
        data={program.outro}
        isOpen={activePhase === position}
        onOpen={() => onCollapsableOpen(position)}
        onClose={() => onCollapsableClose()}
      />
    );
  }

  const enableGiftPurchase = canBuyProgramGift(
    baseProgram?.price,
    baseProgram?.priceType
  );

  const canExtendAccess = canExtendProgramAccess(
    baseProgram?.price,
    baseProgram?.priceType,
    baseProgram.accessStatus
  );

  return (
    <div key="wrapper" className={className}>
      <MetaTags
        title={baseProgram.meta.title || baseProgram.title}
        description={baseProgram.meta.description || baseProgram.description}
        thumbnailUrl={baseProgram.meta.imageUrl || baseProgram.titleImageUrl}
      />
      <ProgramsBreadcrumb
        currentPage={baseProgram.title}
        programCategory={baseProgram.category}
      />
      {[
        ProgramAccessStatus['ExpiringSoon'],
        ProgramAccessStatus['Expired'],
      ].includes(baseProgram?.accessStatus) ? (
        <ProgramExpirationBanner
          programSlug={baseProgram?.slug}
          price={baseProgram?.price}
          accessStatus={baseProgram?.accessStatus}
          programCategory={baseProgram.category}
          accessUntil={userProgramAccess?.accessUntil}
          priceType={baseProgram?.priceType}
          currentUser={currentUser}
        />
      ) : null}
      {!userHasAccess ? (
        <GuestProgramHeader
          title={baseProgram.title}
          description={baseProgram.shortDescription || baseProgram.description}
          instructors={baseProgram.instructors}
          trailer={baseProgram.trailer}
          programImage={baseProgram.titleImageUrl}
          assetURL={baseProgram.summaryAsset}
          themes={baseProgram.themes}
          price={baseProgram.price}
          discount={baseProgram.discount}
          programCategory={baseProgram.category}
          priceType={baseProgram.priceType}
          programSlug={baseProgram.slug}
          duration={baseProgram.duration}
          enableGiftPurchase={enableGiftPurchase}
          getStartedLink={getStartedLink({
            lang,
            slug: baseProgram.slug,
            category: baseProgram.category,
          })}
        />
      ) : (
        <AccessProgramHeaderSection
          title={baseProgram.title}
          description={baseProgram.shortDescription || baseProgram.description}
          duration={baseProgram.duration}
          themes={baseProgram.themes}
          programCategory={baseProgram.category}
          currentVideo={program.currentElement}
          instructors={program.instructors}
          progress={program.progress}
          assetURL={baseProgram.summaryAsset}
          programsAccess={userProgramAccess}
          enableGiftPurchase={enableGiftPurchase}
          canExtendAccess={canExtendAccess}
        />
      )}
      <ProgramsSubHeader
        hasAccess={userHasAccess}
        programCategory={baseProgram.category}
        overviewLink={`/${lang}/${baseProgram.category}/${program.slug}/overview`}
        progressLink={
          userHasAccess
            ? `/${lang}/${baseProgram.category}/${program['currentElement']?.path}`
            : null
        }
      />
      <ProgramPartsBar
        programCategory={baseProgram.category}
        sections={[
          ...(program?.intro
            ? [
                {
                  id: 'Introduction',
                  title: program?.intro?.label || (
                    <Trans id="weeklyProgram.introduction.label" />
                  ),
                },
              ]
            : []),
          ...program.weeks.map((week, i) => ({
            id: week.id,
            title: week.label || (
              <Trans
                id="weeklyProgram.week.number"
                values={{ number: i + 1 }}
              />
            ),
          })),
          ...(program?.outro
            ? [
                {
                  id: 'outro',
                  title: program?.outro?.label,
                },
              ]
            : []),
        ]}
        onItemClick={onCollapsableChange}
        activeSection={activePhase}
      />
      <Container flexDirection="column" px={[20, 20, 10]} mt="30px" mb="30px">
        {collapsables}
      </Container>
    </div>
  );
};

export default CourseOverview;
